import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import contactEmptyMascot from '../../../../assets/contact-empty-mascot.png';
import WhatsAppContactsDataTable from '../../../../components/DataTables/WhatsAppContactsDataTable';
import Icon from '../../../../components/Icon/Icon';
import config from '../../../../config/Constant';
import { getRequestAsync, putRequestAsync } from '../../../../helpers/apiHandler';
import useFBAuthenticated from '../../../../hooks/useFBAuthenticated';
import { showAlert } from '../../../../store/actions/alertAction';
import AddEditContactModal from '../modals/AddEditContactModal';

const WhatsAppContactTabContent = ({ accessToken, wabaId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    has_more: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [userRole, setUserRole] = useState(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContact, setModalContact] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Get user info from Redux store
  const user = useSelector((state) => state.auth.user);
  const { isAuthenticated } = useFBAuthenticated(accessToken);
  const dispatch = useDispatch();

  // Determine user role based on Redux store user data
  const determineUserRole = useCallback(() => {
    // Try different possible paths for the role from Redux store and accessToken
    const userRole = user?.role || accessToken?.user?.role || accessToken?.role;

    if (!userRole) return;

    const role = userRole === 'manager' ? 'manager' : 'agent';
    setUserRole(role);
  }, [user, accessToken]);

  // Determine user role when component mounts
  useEffect(() => {
    if (user?.role || accessToken?.user?.role || accessToken?.role) {
      determineUserRole();
    }
  }, [determineUserRole, user?.role, accessToken?.user?.role, accessToken?.role]);

  // Helper function for authenticated delete requests
  const deleteRequestWithAuth = async (url, authToken) => {
    try {
      const headers = authToken ? { Authorization: authToken } : {};
      const response = await axios.delete(`${config.urls.api_base_url}${url}`, { headers });
      return {
        success: true,
        error: false,
        message: response.data.message || 'Deleted successfully',
        data: response.data.data,
        status_code: response.status
      };
    } catch (error) {
      return {
        success: false,
        error: true,
        message: error.response?.data?.message || error.message || 'Delete failed',
        status_code: error.response?.status || 500
      };
    }
  };

  // Function to get country from phone number
  const getCountryFromPhoneNumber = useCallback((phoneNumber) => {
    if (!phoneNumber) return 'Unknown';

    // Clean the number and ensure it starts with country code
    let cleanNumber = phoneNumber.replace(/\s+/g, '');
    
    // If number doesn't start with +, add it for consistent checking
    if (!cleanNumber.startsWith('+')) {
      cleanNumber = `+${cleanNumber}`;
    }
    
    if (cleanNumber.startsWith('+60')) return 'Malaysia';
    if (cleanNumber.startsWith('+62')) return 'Indonesia';
    if (cleanNumber.startsWith('+65')) return 'Singapore';
    if (cleanNumber.startsWith('+66')) return 'Thailand';
    if (cleanNumber.startsWith('+84')) return 'Vietnam';
    if (cleanNumber.startsWith('+63')) return 'Philippines';
    if (cleanNumber.startsWith('+1')) return 'United States';
    if (cleanNumber.startsWith('+44')) return 'United Kingdom';
    if (cleanNumber.startsWith('+91')) return 'India';
    if (cleanNumber.startsWith('+86')) return 'China';
    if (cleanNumber.startsWith('+81')) return 'Japan';
    if (cleanNumber.startsWith('+82')) return 'South Korea';
    if (cleanNumber.startsWith('+61')) return 'Australia';
    if (cleanNumber.startsWith('+49')) return 'Germany';
    if (cleanNumber.startsWith('+33')) return 'France';
    if (cleanNumber.startsWith('+39')) return 'Italy';
    if (cleanNumber.startsWith('+34')) return 'Spain';
    if (cleanNumber.startsWith('+55')) return 'Brazil';
    if (cleanNumber.startsWith('+52')) return 'Mexico';
    if (cleanNumber.startsWith('+7')) return 'Russia';
    
    return 'Unknown';
  }, []);

  // Function to transform backend contact data to frontend format
  const transformContactData = useCallback((backendContact) => ({
    id: backendContact.id,
    whatsappNumber: backendContact.phone_number,
    name: backendContact.display_name || `${backendContact.first_name || ''} ${backendContact.last_name || ''}`.trim() || backendContact.phone_number,
    country: getCountryFromPhoneNumber(backendContact.phone_number),
    email: backendContact.email,
    company: backendContact.company,
    status: backendContact.contact_status,
    assignedAgent: backendContact.assigned_agent_name
  }), [getCountryFromPhoneNumber]);

  // Function to fetch contacts from API
  const fetchContacts = useCallback(async (search = '', page = 1) => {
    if (!isAuthenticated || !accessToken?.token || !userRole || !wabaId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const offset = (page - 1) * pagination.limit;
      
      let endpoint;
      // Use appropriate endpoint based on user role
      if (userRole === 'manager') {
        endpoint = '/v2/whatsapp/manager/contacts';
      } else {
        endpoint = '/v2/whatsapp/agent/contacts';
      }

      const response = await getRequestAsync(endpoint, {
        headers: {
          Authorization: accessToken.token
        },
        params: {
          waba_id: wabaId,
          limit: pagination.limit,
          offset: offset,
          sort_by: 'last_contact_at',
          sort_order: 'DESC',
          ...(search.trim() && { search: search.trim() })
        }
      });

      if (response.success) {
        const transformedContacts = response.data.contacts.map(transformContactData);
        setContacts(transformedContacts);

        // Update pagination state from API response
        const apiPagination = response.data.pagination;
        if (apiPagination) {
          setPagination({
            total: apiPagination.total,
            limit: apiPagination.limit,
            offset: apiPagination.offset,
            has_more: apiPagination.has_more
          });
        } else {
          // Fallback if no pagination data from API
          setPagination(prev => ({
            ...prev,
            total: prev.total, // Keep existing total if not provided
            offset: offset
          }));
        }
      } else {
        throw new Error(response.message || 'Failed to fetch contacts');
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      const errorMessage = err.message || 'Failed to load contacts';
      setError(errorMessage);
      setContacts([]);
      dispatch(showAlert(errorMessage, 'danger'));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, accessToken?.token, userRole, wabaId, pagination.limit, transformContactData, dispatch]);

  useEffect(() => {
    if (isAuthenticated && userRole) {
      fetchContacts();
    }
  }, [isAuthenticated, userRole, fetchContacts]);

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchContacts(searchTerm, page);
  };

  const handleRowsPerPageChange = (newPerPage, page) => {
    setPagination(prev => ({
      ...prev,
      limit: newPerPage,
      offset: (page - 1) * newPerPage
    }));
    setCurrentPage(page);
    // Fetch with updated pagination
    setTimeout(() => fetchContacts(searchTerm, page), 0);
  };

  // CRUD Operations
  const handleEditContact = (contact) => {
    // Transform contact data to match modal expectations
    const modalContactData = {
      phone: contact.whatsappNumber,
      contactId: contact.id,
      contactData: {
        displayName: contact.name
      }
    };
    
    setModalContact(modalContactData);
    setModalError(null);
    setIsModalOpen(true);
  };



  const handleModalSave = async (contactData, contactId) => {
    if (!accessToken?.token || !userRole || !contactId) {
      setModalError('Missing authentication or contact information');
      return;
    }

    setModalLoading(true);
    setModalError(null);

    try {
      // Update existing contact
      const endpoint = `/v2/whatsapp/${userRole}/contacts/${contactId}`;
      const response = await putRequestAsync(endpoint, contactData, accessToken.token);

      if (response.success) {
        // Close modal and refresh contacts
        setIsModalOpen(false);
        setModalContact(null);
        await fetchContacts(searchTerm, currentPage);
      } else {
        setModalError(response.message || 'Failed to update contact');
      }
    } catch (err) {
      console.error('Error updating contact:', err);
      setModalError(err.message || 'Failed to update contact');
    } finally {
      setModalLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalContact(null);
    setModalError(null);
    setModalLoading(false);
  };

  const handleDeleteContact = async (contact) => {
    // Check authentication
    if (!isAuthenticated || !accessToken?.token) {
      setError('Please log in to delete contacts');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${contact.name}?`)) {
      return;
    }

    try {
      let endpoint;
      // Use appropriate endpoint based on user role
      if (userRole === 'manager') {
        endpoint = `/v2/whatsapp/manager/contacts/${contact.id}`;
      } else {
        endpoint = `/v2/whatsapp/agent/contacts/${contact.id}`;
      }

      const response = await deleteRequestWithAuth(endpoint, accessToken.token);

      if (response.success) {
        // Refresh contacts list
        fetchContacts(searchTerm, currentPage);
        alert('Contact deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete contact');
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError(`Failed to delete contact: ${err.message}`);
    }
  };

  const handleExportContacts = (selectedContacts) => {
    if (!selectedContacts || selectedContacts.length === 0) return;

    try {
      // Create CSV content for selected contacts
      const headers = ['WhatsApp Number', 'Name', 'Country', 'Email', 'Company', 'Status'];
      const csvContent = [
        headers.join(','),
        ...selectedContacts.map(contact => [
          `"${contact.whatsappNumber}"`,
          `"${contact.name}"`,
          `"${contact.country}"`,
          `"${contact.email || ''}"`,
          `"${contact.company || ''}"`,
          `"${contact.status || 'active'}"`
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `selected_contacts_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export selected contacts');
    }
  };

  // Bulk Operations
  const handleSelectContact = (contact, isSelected) => {
    if (isSelected) {
      setSelectedContacts(prev => [...prev, contact]);
    } else {
      setSelectedContacts(prev => prev.filter(c => c.id !== contact.id));
    }
  };

  const handleSelectAllContacts = (isSelected) => {
    if (isSelected) {
      setSelectedContacts([...filteredContacts]);
    } else {
      setSelectedContacts([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedContacts.length} contacts?`)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const contact of selectedContacts) {
        try {
          let endpoint;
          if (userRole === 'manager') {
            endpoint = `/v2/whatsapp/manager/contacts/${contact.id}`;
          } else {
            endpoint = `/v2/whatsapp/agent/contacts/${contact.id}`;
          }

      const response = await deleteRequestWithAuth(endpoint, accessToken.token);
          if (response.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (err) {
          console.error(`Error deleting contact ${contact.id}:`, err);
          errorCount++;
        }
      }

      // Refresh contacts list
      fetchContacts(searchTerm, currentPage);
      setSelectedContacts([]);

      if (errorCount === 0) {
        dispatch(showAlert(`Successfully deleted ${successCount} contacts`, 'info'))
      } else {
        dispatch(showAlert(`Deleted ${successCount} contacts. ${errorCount} failed.`, 'warning'))
      }
    } catch (err) {
      console.error('Error in bulk delete:', err);
      setError('Failed to delete selected contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkExport = () => {
    if (selectedContacts.length === 0) return;
    handleExportContacts(selectedContacts);
  };

  // Filter contacts based on search term (client-side fallback)
  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.whatsappNumber.includes(searchTerm) ||
    contact.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Add selection state to contacts for display (computed on-the-fly to avoid re-render loops)
  const contactsWithSelection = filteredContacts.map(contact => ({
    ...contact,
    isSelected: selectedContacts.some(c => c.id === contact.id)
  }));

  // Debounced search function
  const debouncedSearch = (() => {
    let timeoutId;
    return (searchValue) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fetchContacts(searchValue, 1); // Reset to first page on search
        setCurrentPage(1);
      }, 500); // 500ms debounce
    };
  })();

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleExport = () => {
    if (contacts.length === 0) return;

    try {
      // Create CSV content
      const headers = ['WhatsApp Number', 'Name', 'Country', 'Email', 'Company', 'Status'];
      const csvContent = [
        headers.join(','),
        ...contacts.map(contact => [
          `"${contact.whatsappNumber}"`,
          `"${contact.name}"`,
          `"${contact.country}"`,
          `"${contact.email || ''}"`,
          `"${contact.company || ''}"`,
          `"${contact.status || 'active'}"`
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `whatsapp_contacts_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export contacts');
    }
  };

  const renderEmptyState = () => {
    const hasSearchTerm = searchTerm.trim().length > 0;

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 0',
        textAlign: 'center',
        minHeight: '500px'
      }}>
        <img
          src={contactEmptyMascot}
          alt="No contacts mascot"
          style={{
            width: '215px',
            height: '215px',
            marginBottom: '24px'
          }}
        />
        <div style={{
          maxWidth: '544px'
        }}>
          <h3 style={{
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '18px',
            lineHeight: '1.5em',
            letterSpacing: '0.25%',
            color: '#344054',
            marginBottom: '8px'
          }}>
            {hasSearchTerm ? 'No contacts found' : 'No contacts yet'}
          </h3>
          <p style={{
            fontFamily: 'Poppins',
            fontWeight: '400',
            fontSize: '14px',
            lineHeight: '1.5em',
            color: '#344054',
            margin: '0',
            maxWidth: '554px'
          }}>
            {hasSearchTerm
              ? `No contacts match your search for "${searchTerm}". Try adjusting your search terms.`
              : 'Once a customer messages your business, their contact can be added and will appear here automatically.'
            }
          </p>
          {hasSearchTerm && (
            <Button
              variant="outline-primary"
              className="mt-3"
              onClick={() => {
                setSearchTerm('');
                fetchContacts('', 1);
                setCurrentPage(1);
              }}
            >
              Clear Search
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderContactHeader = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'stretch',
      gap: '46px',
      padding: '20px 32px',
      borderRadius: '0px 20px 0px 0px',
    }}>
      {/* Left side - Description */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        flex: 1
      }}>
        <p style={{
          fontFamily: 'Poppins',
          fontWeight: '400',
          fontSize: '12px',
          lineHeight: '1.5em',
          color: '#737C8A',
          margin: '0',
          whiteSpace: 'nowrap'
        }}>
          View and manage all your WhatsApp Business contacts in one place.
        </p>
      </div>

      {/* Right side - Search and Export */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '16px',
        width: '782px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: '30px',
          width: '317px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: '10px',
            padding: '4px 8px',
            backgroundColor: '#F5F7FA',
            border: '1px solid #E5E7F0',
            borderRadius: '4px',
            flex: 1
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon
                name="search"
                color="#737C8A"
                width={16}
                height={16}
              />
              <Form.Control
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={handleSearchChange}
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  padding: '0',
                  fontFamily: 'Poppins',
                  fontWeight: '500',
                  fontSize: '14px',
                  lineHeight: '1.5em',
                  letterSpacing: '0.25%',
                  color: '#737C8A'
                }}
              />
            </div>
          </div>
        </div>

        <Button
          onClick={handleExport}
          disabled={contacts.length === 0 || loading}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 18px',
            backgroundColor: (contacts.length === 0 || loading) ? '#BDC3C7' : '#0BA9E0',
            border: 'none',
            borderRadius: '4px',
            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
            fontFamily: 'Poppins',
            fontWeight: '500',
            fontSize: '13px',
            lineHeight: '1.5em',
            color: '#FFFFFF'
          }}
        >
          <Icon
            name="upload"
            color="#FFFFFF"
            width={17}
            height={17}
          />
          Export
        </Button>
      </div>
    </div>
  );

  // Show loading state
  if (loading && contacts.length === 0) {
    return (
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        borderRadius: '0px 20px 20px 20px',
        margin: '0 40px 40px 40px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      borderRadius: '0px 20px 20px 20px',
      margin: '0 40px 40px 40px',
      maxWidth: 'calc(100vw - 80px)', // Ensure it doesn't exceed viewport width minus margins
      overflow: 'hidden' // Prevent content from overflowing
    }}>
      <div style={{ padding: '0' }}>
        {renderContactHeader()}

        {/* Error Alert */}
        {error && (
          <div style={{ padding: '0 32px 16px 32px' }}>
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <strong>Error:</strong> {error}
              <Button
                variant="outline-danger"
                size="sm"
                className="ms-2"
                onClick={() => fetchContacts(searchTerm, currentPage)}
              >
                Retry
              </Button>
            </Alert>
          </div>
        )}

        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '0px 20px 20px 20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          gap: '20px',
          width: '100%', // Ensure full width usage within container
          overflowX: 'auto' // Allow horizontal scrolling if needed
        }}>
          {contacts.length === 0 && !loading ? renderEmptyState() : (
            <div style={{
              width: '100%',
              overflowX: 'auto' // Allow table to scroll horizontally if needed
            }}>
              <WhatsAppContactsDataTable
                data={contactsWithSelection}
                loadingData={loading}
                pagination={pagination}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onEdit={handleEditContact}
                onDelete={handleDeleteContact}
                onExport={handleExportContacts}
                onSelectContact={handleSelectContact}
                onSelectAllContacts={handleSelectAllContacts}
                selectedContacts={selectedContacts}
              />
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Contact Modal */}
      <AddEditContactModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        contact={modalContact}
        isLoading={modalLoading}
        error={modalError}
      />
    </div>
  );
};

export default WhatsAppContactTabContent;
